import { logD } from "@blacknut/logging/dist";
import React, {
  PropsWithChildren,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
} from "react";
import ReactModal from "react-modal";
import { Location, NavigateFunction } from "react-router";

const TAG = "Modal";

export interface ModalSubscriptionOpt {
  preventGoBack?: boolean;
}
export interface ModalSubscription {
  remove: (e?: ModalSubscriptionOpt) => void;
}

export const ModalContext = React.createContext<{
  modals: React.ComponentType<Partial<ReactModal.Props>>[];
  push: <TOriginalProps extends Omit<ReactModal.Props, "isOpen">>(
    modal: React.ComponentType<
      TOriginalProps & Pick<ReactModal.Props, "isOpen">
    >
  ) => ModalSubscription;
  Modal?: React.ComponentType<Partial<ReactModal.Props>>;
  activeElementBeforeModalShown?: HTMLElement;
}>({
  modals: [],
  push: () => {
    console.log("nop");
    throw new Error(
      "Attempt to push modal, but there is no ModalContextProvider"
    );
  },
});

export const ModalContextProvider = (
  props: PropsWithChildren<{ navigate: NavigateFunction; location: Location }>
) => {
  const { navigate, location } = props;
  const [modals, setModals] = useState(
    [] as React.ComponentType<Partial<ReactModal.Props>>[]
  );
  const MyModal =
    modals && modals.length > 0 ? modals[modals.length - 1] : undefined;
  const navigateRef = useRef(navigate);
  const locationRef = useRef(location);
  const activeElement = useRef<HTMLElement>();
  const push = useCallback(
    <TOriginalProps extends Omit<ReactModal.Props, "isOpen">>(
      modal: React.ComponentType<TOriginalProps>
    ) => {
      logD(TAG, "Pushing new modal");
      document.body.classList.add("noScroll");

      setModals((modals) => {
        const res = modals.filter((m) => m.toString() !== modal.toString());
        return [
          ...res,
          modal as React.ComponentType<Partial<ReactModal.Props>>,
        ];
      });

      activeElement.current = document.activeElement as HTMLElement;

      if (locationRef?.current?.pathname !== "/modal") {
        logD(TAG, "Pushing new location");
        navigateRef.current("/modal", {
          state: {
            background: locationRef.current,
          },
        });
      }

      return {
        remove: (e?: ModalSubscriptionOpt) => {
          logD(TAG, "Poping modal");

          document.body.classList.remove("noScroll");
          const { preventGoBack = false } = e || {};

          setModals((modals) => {
            const res = modals.filter((m) => m !== modal);
            return res;
          });
          if (!preventGoBack) {
            logD(TAG, "Poping location");

            navigateRef.current(-1);
          }
        },
      };
    },
    []
  );
  useEffect(() => {
    locationRef.current = location;
  }, [MyModal, location]);

  return (
    <ModalContext.Provider
      value={{
        modals,
        push,
        Modal: MyModal,
        activeElementBeforeModalShown: activeElement.current,
      }}
    >
      {props.children}
    </ModalContext.Provider>
  );
};

export function useModal() {
  return useContext(ModalContext);
}
